import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@core-ui/Components/ui/badge';
import { cn } from '@core-ui-lib/utils';

import { Play, Pause, Loader2, CirclePlus } from 'lucide-react';

import { SearchInput } from '@ui/shared/searchInput';
import { usePaginationData } from '@hooks/usePaginationData';
import { PaginationControls } from '@ui/shared/paginationControls';
import useVoicemailApi from '@features/voicemail/hooks/useVoicemailApi';
import { useSessionStore } from '@features/auth';

import type { VoicemailDto } from '../../types/wizard.types';
import useWizardStore from '../../store/useWizardStore';
import VoicemailRecordPanel from '../inline-create/VoicemailRecordPanel';

interface SelectVoicemailStepProps {
  onCreateHandlerChange?: (handler: (() => void) | null) => void;
  onCanCreateChange?: (canCreate: boolean) => void;
}

export default function SelectVoicemailStep ({
  onCreateHandlerChange,
  onCanCreateChange,
}: SelectVoicemailStepProps): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useSessionStore();
  const { getVoicemailsBySdrIdAsync } = useVoicemailApi();

  const { selectedVoicemail, setVoicemail, stepMode, setStepMode, nextStep } = useWizardStore();

  const [voicemails, setVoicemails] = useState<VoicemailDto[]>([]);
  const [filteredVoicemails, setFilteredVoicemails] = useState<VoicemailDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [audioDurations, setAudioDurations] = useState<Record<string, number>>({});
  const [playbackProgress, setPlaybackProgress] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const activeMode = stepMode[3] ?? 'select';

  // In record mode, "Create & Continue" is handled by the recording stop + onCreated flow
  // The navigation button shouldn't apply here since recording has its own controls
  useEffect(() => {
    onCanCreateChange?.(false);
    onCreateHandlerChange?.(null);
    // Only run on mode change, not on callback identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode]);

  const fetchVoicemails = useCallback(async (): Promise<void> => {
    if (currentUser?.userId === undefined) {
      return;
    }

    setIsLoading(true);
    const result = await getVoicemailsBySdrIdAsync(currentUser.userId);
    if (result.success && result.data !== undefined) {

      const sortedData = [...result.data].sort((a, b) => {
        const dateA = new Date( a.createdAt ?? 0).getTime();
        const dateB = new Date(b.createdAt ?? 0).getTime();
        return dateB - dateA;
      });

      setVoicemails(sortedData);
      setFilteredVoicemails(sortedData);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.userId]);

  useEffect(() => {
    void fetchVoicemails();
  }, [fetchVoicemails]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredVoicemails(voicemails);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredVoicemails(
        voicemails.filter((vm) => vm.title.toLowerCase().includes(query)),
      );
    }
  }, [searchQuery, voicemails]);

  // Preload audio durations
  useEffect(() => {
    const loadDurations = async (): Promise<void> => {
      const newDurations: Record<string, number> = {};

      await Promise.all(
        voicemails.map(async (vm) => {
          const vmId = vm.id ?? vm._id;
          const audioUrl = vm.recordingUrl ?? vm.url;

          if (vmId === undefined || audioUrl === undefined) {
            return;
          }

          const apiDuration = vm.durationSec ?? vm.duration ?? vm.recordingDuration ?? vm.duration_seconds ?? vm.length;
          if (apiDuration !== undefined && apiDuration !== null && Number(apiDuration) > 0) {
            newDurations[vmId] = Math.floor(Number(apiDuration));
            return;
          }

          try {
            const audio = new Audio();
            audio.preload = 'metadata';

            await new Promise<void>((resolve) => {
              audio.onloadedmetadata = () => {
                if (Number.isFinite(audio.duration) && audio.duration > 0) {
                  newDurations[vmId] = Math.floor(audio.duration);
                }
                resolve();
              };
              audio.onerror = () => {
                resolve();
              };
              setTimeout(resolve, 3000);
              audio.src = audioUrl;
            });
          } catch {
            // Ignore errors
          }
        }),
      );

      if (Object.keys(newDurations).length > 0) {
        setAudioDurations((prev) => ({ ...prev, ...newDurations }));
      }
    };

    if (voicemails.length > 0) {
      void loadDurations();
    }
  }, [voicemails]);

  const {
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    currentData: paginatedVoicemails,
    totalItems,
  } = usePaginationData<VoicemailDto>({
    mode: 'client',
    data: filteredVoicemails,
    initialRowsPerPage: 5,
  });

  useEffect(() => {
    return () => {
      if (audioRef.current !== null) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const handleSelectVoicemail = (vm: VoicemailDto): void => {
    const vmId = vm.id ?? vm._id;
    const selectedId = selectedVoicemail?.id ?? selectedVoicemail?._id;

    if (selectedId === vmId) {
      setVoicemail(null);
    } else {
      setVoicemail(vm);
    }
  };

  const handlePlayPause = (vm: VoicemailDto): void => {
    const vmId = vm.id ?? vm._id;
    const audioUrl = vm.recordingUrl ?? vm.url;

    if (audioUrl === undefined) {
      return;
    }

    if (playingId === vmId) {
      if (audioRef.current !== null) {
        audioRef.current.pause();
      }
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current);
      }
      setPlayingId(null);
      setPlaybackProgress(0);
    } else {
      if (audioRef.current !== null) {
        audioRef.current.pause();
      }
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current);
      }

      audioRef.current = new Audio(audioUrl);

      audioRef.current.onloadedmetadata = () => {
        if (audioRef.current !== null && vmId !== undefined) {
          const duration = audioRef.current.duration;
          if (Number.isFinite(duration) && duration > 0) {
            setAudioDurations((prev) => ({ ...prev, [vmId]: Math.floor(duration) }));
          }
        }
      };

      audioRef.current.play().catch(() => {});

      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current !== null) {
          const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
          setPlaybackProgress(Number.isFinite(progress) ? progress : 0);
        }
      }, 100);

      audioRef.current.onended = () => {
        setPlayingId(null);
        setPlaybackProgress(0);
        if (progressIntervalRef.current !== null) {
          clearInterval(progressIntervalRef.current);
        }
      };
      setPlayingId(vmId ?? null);
    }
  };

  const getDurationSec = (vm: VoicemailDto): number => {
    const vmId = vm.id ?? vm._id ?? '';
    if (audioDurations[vmId] !== undefined && audioDurations[vmId] > 0) {
      return audioDurations[vmId];
    }
    const raw = vm.durationSec ?? vm.duration ?? vm.recordingDuration ?? vm.duration_seconds ?? vm.length ?? 0;
    const num = typeof raw === 'string' ? parseFloat(raw) : raw;
    return Number.isFinite(num) ? Math.floor(num) : 0;
  };

  const formatDuration = (seconds: number): string => {
    if (seconds <= 0) {
      return '—';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const generateWaveformBars = (vmId: string): number[] => {
    const seed = vmId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const bars: number[] = [];
    for (let i = 0; i < 20; i++) {
      const height = 20 + ((seed * (i + 1) * 7) % 80);
      bars.push(height);
    }
    return bars;
  };

  const handleVoicemailCreated = (vm: VoicemailDto): void => {
    void fetchVoicemails();
    setVoicemail(vm);
    setStepMode(3, 'select');
    nextStep();
  };

  const handleTabChange = (mode: string): void => {
    // Stop audio playback when switching tabs
    if (audioRef.current !== null) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (progressIntervalRef.current !== null) {
      clearInterval(progressIntervalRef.current);
    }
    setPlayingId(null);
    setPlaybackProgress(0);
    setStepMode(3, mode as 'select' | 'create');
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold">{t('wizard.selectVoicemail.title')}</h3>
        <p className="text-sm text-muted-foreground">
          {activeMode === 'create'
            ? t('wizard.inlineCreate.selectOrRecordVoicemail', 'Select a voicemail recording or record a new one')
            : t('wizard.selectVoicemail.description')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4">
        <button
          type="button"
          onClick={() => {
            handleTabChange('select'); 
          }}
          className={cn(
            'px-4 py-2 rounded-md text-sm font-medium transition-colors',
            activeMode === 'select'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/60 text-muted-foreground hover:bg-muted',
          )}
        >
          {t('wizard.inlineCreate.selectExisting', 'Select Existing')}
        </button>
        <button
          type="button"
          onClick={() => {
            handleTabChange('create'); 
          }}
          className={cn(
            'flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors',
            activeMode === 'create'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/60 text-muted-foreground hover:bg-muted',
          )}
        >
          <CirclePlus className="mr-2 h-4 w-4" />
          {t('wizard.inlineCreate.recordNew', 'Record New')}
        </button>
      </div>

      {activeMode === 'select' ? (
        <div className="space-y-4">
          <SearchInput
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onClear={() => {
              setSearchQuery('');
            }}
            placeholder={t('wizard.selectVoicemail.searchPh')}
          />

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredVoicemails.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              {t('wizard.selectVoicemail.empty')}
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-border">
                {paginatedVoicemails.map((vm) => {
                  const vmId = vm.id ?? vm._id;
                  const selectedId = selectedVoicemail?.id ?? selectedVoicemail?._id;

                  return (
                    <button
                      key={vmId}
                      type="button"
                      onClick={() => {
                        handleSelectVoicemail(vm);
                      }}
                      className={cn(
                        'w-full flex items-center gap-4 p-4 text-left transition-colors hover:bg-muted/40 border-b border-border/60 last:border-b-0',
                        selectedId === vmId && 'bg-muted/60',
                      )}
                    >
                      <div
                        className={cn(
                          'h-5 w-5 rounded-full border-2 flex-shrink-0 transition-colors',
                          selectedId === vmId
                            ? 'border-primary bg-primary'
                            : 'border-muted-foreground',
                        )}
                      >
                        {selectedId === vmId && (
                          <div className="h-full w-full flex items-center justify-center">
                            <div className="h-2 w-2 rounded-full bg-white" />
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayPause(vm);
                        }}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        {playingId === vmId ? (
                          <Pause className="h-5 w-5" />
                        ) : (
                          <Play className="h-5 w-5 ml-0.5" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{vm.title}</span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {t('wizard.selectVoicemail.updated')} {vm.updatedAt !== undefined ? new Date(vm.updatedAt).toLocaleDateString() : '—'}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-[2px] h-6 w-28">
                          {generateWaveformBars(vmId ?? '').map((height, index) => {
                            const barProgress = (index / 20) * 100;
                            const isActive = playingId === vmId && barProgress <= playbackProgress;
                            return (
                              <div
                                key={index}
                                className={cn(
                                  'w-1 rounded-full transition-all duration-150',
                                  isActive ? 'bg-primary' : 'bg-muted-foreground/30',
                                  playingId === vmId && barProgress <= playbackProgress + 5 && barProgress > playbackProgress && 'bg-primary/60',
                                )}
                                style={{ height: `${height}%` }}
                              />
                            );
                          })}
                        </div>
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {formatDuration(getDurationSec(vm))}
                        </span>
                      </div>

                      {selectedId === vmId && (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          {t('wizard.selected')}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>

              {totalItems > 0 && (
                <PaginationControls
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={(p) => {
                    setCurrentPage(Math.max(1, Math.min(p, Math.ceil(totalItems / rowsPerPage))));
                  }}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={(n) => {
                    setRowsPerPage(n);
                    setCurrentPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 15]}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <VoicemailRecordPanel onCreated={handleVoicemailCreated} />
      )}
    </div>
  );
}
